import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Download, Search, Gamepad2, Wrench, Code, BarChart, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Software() {
  const [software, setSoftware] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');

  useEffect(() => {
    fetchSoftware();
  }, []);

  const fetchSoftware = async () => {
    try {
      const response = await axios.get(`${API}/software`);
      setSoftware(response.data);
    } catch (error) {
      console.error('Failed to fetch software:', error);
      toast.error('Failed to load software');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (item) => {
    try {
      const response = await axios.post(`${API}/software/${item.id}/download`);
      window.open(response.data.download_url, '_blank');
      toast.success(`Downloading ${item.title}`);
      // Update local download count
      setSoftware(prev => prev.map(s => 
        s.id === item.id ? { ...s, downloads: s.downloads + 1 } : s
      ));
    } catch (error) {
      toast.error('Download failed');
    }
  };

  const getCategoryIcon = (cat) => {
    switch (cat) {
      case 'games': return Gamepad2;
      case 'utilities': return Wrench;
      case 'development': return Code;
      case 'productivity': return BarChart;
      default: return Download;
    }
  };

  const filteredSoftware = software.filter(item => {
    const matchesSearch = item.title.toLowerCase().includes(search.toLowerCase()) ||
      item.description.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = category === 'all' || item.category === category;
    return matchesSearch && matchesCategory;
  });

  const categories = ['all', 'games', 'utilities', 'development', 'productivity'];

  return (
    <div className="min-h-screen pt-24 pb-12">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-900/20 via-gray-950 to-gray-950 -z-10" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4 font-['Outfit']">
            Free <span className="text-gradient">Software</span>
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Download working sample software and games. All free, no strings attached.
          </p>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search software..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-gray-800/50 border-gray-700"
              data-testid="software-search-input"
            />
          </div>
          <Tabs value={category} onValueChange={setCategory}>
            <TabsList className="bg-gray-800/50">
              {categories.map(cat => (
                <TabsTrigger 
                  key={cat} 
                  value={cat}
                  className="capitalize data-[state=active]:bg-blue-600"
                >
                  {cat}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        {/* Software Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <Card key={i} className="bg-gray-900/50 border-white/10 animate-pulse">
                <div className="h-48 bg-gray-800" />
                <CardContent className="p-6">
                  <div className="h-6 bg-gray-800 rounded mb-2" />
                  <div className="h-4 bg-gray-800 rounded w-2/3" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredSoftware.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-400">No software found matching your criteria.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredSoftware.map((item) => {
              const CategoryIcon = getCategoryIcon(item.category);
              return (
                <Card 
                  key={item.id} 
                  className="bg-gray-900/50 border-white/10 hover:border-blue-500/30 transition-colors overflow-hidden group card-hover"
                  data-testid={`software-card-${item.id}`}
                >
                  {item.screenshot_url && (
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={item.screenshot_url}
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-gray-900 to-transparent" />
                      <Badge className="absolute top-3 left-3 capitalize bg-blue-500/20 text-blue-400 border-blue-500/30">
                        <CategoryIcon className="w-3 h-3 mr-1" />
                        {item.category}
                      </Badge>
                    </div>
                  )}
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-white mb-2 font-['Outfit']">{item.title}</h3>
                    <p className="text-gray-400 text-sm mb-4 line-clamp-2">{item.description}</p>
                    
                    <div className="flex flex-wrap gap-2 mb-4">
                      {item.tags?.slice(0, 3).map((tag, i) => (
                        <Badge key={i} variant="outline" className="text-xs border-gray-700 text-gray-400">
                          {tag}
                        </Badge>
                      ))}
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500 flex items-center gap-1">
                        <Download className="w-4 h-4" />
                        {item.downloads?.toLocaleString()} downloads
                      </span>
                      <Button
                        onClick={() => handleDownload(item)}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        size="sm"
                        data-testid={`download-btn-${item.id}`}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Download
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* CTA */}
        <div className="mt-16 text-center">
          <Card className="inline-block bg-gray-900/50 border-white/10 p-8">
            <h3 className="text-xl font-semibold mb-2 font-['Outfit']">Are you a developer?</h3>
            <p className="text-gray-400 mb-4">Share your software with our community</p>
            <Link to="/register">
              <Button className="bg-green-500 hover:bg-green-600 text-black">
                Join as Developer
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </Card>
        </div>
      </div>
    </div>
  );
}
